import { useCallback, useMemo } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Card } from "@/modern/components/ui/card";
import { Button } from "@/modern/components/ui/button";
import {
  Wrench,
  ArrowLeftRight,
  Building2,
  DollarSign,
  ContactRound,
  Users,
  AlertCircle,
  ChevronRight,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { IMPORT_TYPES, IMPORT_HEADERS } from "@/other/constants/OtherKeys";
import {
  AsyncTaskUI,
  useAsyncTaskUIContext,
} from "@/modern/components/async-tasks/async-task-ui";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import type { ImportDataTypeId } from "@/modern/components/async-tasks/types";
import {
  getTaskStatusMessage,
  matchBoardCsvImportExportTasks,
  matchImportEditorCsvTask,
  resolveImportDataTypeId,
} from "@/modern/components/async-tasks/utils";
import { cn } from "@/modern/utils";

/** Minimal pending indicator — rendered by AsyncTaskUI.Loader. */
function ImportExportPendingIndicator() {
  return (
    <Loader2
      className="h-4 w-4 shrink-0 animate-spin text-muted-foreground/70"
      aria-hidden
    />
  );
}

interface ImportExportButtonProps {
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  boardId: number;
  dataTypeId: ImportDataTypeId;
}

const ImportExportButton = ({
  icon: Icon,
  title,
  description,
  onClick,
  disabled,
  boardId,
  dataTypeId,
}: ImportExportButtonProps) => {
  const { pendingTasks } = useAsyncTaskUIContext();

  const matchDataTypeTask = useCallback(
    (task: AsyncTask) => matchImportEditorCsvTask(task, boardId, dataTypeId),
    [boardId, dataTypeId],
  );

  const activeTask = useMemo(
    () => pendingTasks.find(matchDataTypeTask),
    [pendingTasks, matchDataTypeTask],
  );
  const isActive = activeTask != null;
  const statusMessage = activeTask ? getTaskStatusMessage(activeTask) : null;

  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "group h-auto w-full py-4 px-5",
        "flex items-center gap-4 justify-start text-left",
        "transition-colors duration-200",
        "hover:border-primary/40 hover:bg-accent/30",
        isActive && "border-primary/30",
      )}
      aria-busy={isActive}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
        <Icon className="h-5 w-5 text-primary" aria-hidden />
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-semibold text-base text-foreground mb-0.5 truncate">
          {title}
        </div>
        {isActive && statusMessage ? (
          <p
            className="truncate text-xs text-muted-foreground"
            title={statusMessage}
            role="status"
            aria-live="polite"
          >
            {statusMessage}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>

      {isActive ? (
        <AsyncTaskUI.Loader forTask={matchDataTypeTask}>
          <ImportExportPendingIndicator />
        </AsyncTaskUI.Loader>
      ) : (
        <ChevronRight
          className="h-4 w-4 shrink-0 text-muted-foreground/25 opacity-0 -translate-x-0.5 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0"
          aria-hidden
        />
      )}
    </Button>
  );
};

const IMPORT_EXPORT_ITEMS = [
  {
    importType: IMPORT_TYPES.resources,
    importHeader: IMPORT_HEADERS.resources,
    icon: Wrench,
    title: "Resources",
    description: "Import or export resource data",
  },
  {
    importType: IMPORT_TYPES.tasks,
    importHeader: IMPORT_HEADERS.tasks,
    icon: ArrowLeftRight,
    title: "Tasks",
    description: "Import or export task data",
  },
  {
    importType: IMPORT_TYPES.clients,
    importHeader: IMPORT_HEADERS.clients,
    icon: Building2,
    title: "Clients",
    description: "Import or export client data",
  },
  {
    importType: IMPORT_TYPES.taskBudget,
    importHeader: IMPORT_HEADERS.taskBudget,
    icon: DollarSign,
    title: "Budget",
    description: "Import or export budget data",
  },
  {
    importType: IMPORT_TYPES.contacts,
    importHeader: IMPORT_HEADERS.contacts,
    icon: ContactRound,
    title: "Contacts",
    description: "Import or export contact data",
  },
  {
    importType: IMPORT_TYPES.boardUsers,
    importHeader: IMPORT_HEADERS.boardUsers,
    icon: Users,
    title: "Board Users",
    description: "Import or export board user data",
  },
] as const;

const BoardImportExportDetails = ({ subcategory }: SubcategoryDetailProps) => {
  const { user, editorHandler, selectedBoard } = useSwivuBryntumContext();

  const canImport = user?.can(GRANTS.grant_three);
  const isDemoUser = user?.isDemoUser;
  const isDisabled = isDemoUser || !canImport;

  const boardId = selectedBoard?.id as number | undefined;

  const taskFilter = useCallback(
    (task: AsyncTask) =>
      boardId != null && matchBoardCsvImportExportTasks(task, boardId),
    [boardId],
  );

  const openImportEditor = (importType: string, importHeader: string) => {
    if (!editorHandler) return;

    editorHandler.openEditor(
      EDITOR_KEYS.importEditor,
      // @ts-expect-error - editorHandler.openEditor second parameter accepts null
      null,
      {
        importType,
        canEdit: user && user.can(GRANTS.grant_one),
        importHeader,
        labelIndex: 12,
      },
      { useExisting: true, by: "recordType" },
    );
  };

  if (boardId == null) {
    return null;
  }

  return (
    <AsyncTaskUI filter={taskFilter}>
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {subcategory.label}
          </h2>
          <p className="text-sm text-muted-foreground">
            {subcategory.description}
          </p>
        </div>

        <div className="space-y-5 max-w-5xl">
          {isDisabled && (
            <Card className="py-3 px-4 border-primary/20 bg-primary/5">
              <div className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <AlertCircle className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-foreground">
                    {isDemoUser
                      ? "Import/Export is not available for demo users."
                      : "You need at least Contributor permissions to import or export data."}
                  </p>
                </div>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {IMPORT_EXPORT_ITEMS.map((item) => {
              const dataTypeId = resolveImportDataTypeId(item.importType);
              if (dataTypeId == null) return null;

              return (
                <ImportExportButton
                  key={item.importType}
                  icon={item.icon}
                  title={item.title}
                  description={item.description}
                  boardId={boardId}
                  dataTypeId={dataTypeId}
                  onClick={() =>
                    openImportEditor(item.importType, item.importHeader)
                  }
                  disabled={isDisabled}
                />
              );
            })}
          </div>

          <Card className="py-3 px-4 border-primary/20 bg-primary/5">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <AlertCircle className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  Each import/export option opens a dialog where you can upload
                  files, download templates, or export existing data. Make sure
                  your data follows the required format for successful imports.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AsyncTaskUI>
  );
};

export default BoardImportExportDetails;
